import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError, MatInput } from '@angular/material/input';
import moment from 'moment';
import { DATE_FORMAT } from '../../shared/constants/date.constants';
import { MatDialog } from '@angular/material/dialog';
import { ModifyNameDialogComponent } from '../../shared/components/dialogs/modify-name-dialog/modify-name-dialog.component';
import { DevService } from '../../shared/services/dev.service';
import { SavePhoneDialogComponent } from '../../shared/components/dialogs/save-phone-dialog/save-phone-dialog.component';
import { ModifyEmailDialogComponent } from '../../shared/components/dialogs/modify-email-dialog/modify-email-dialog.component';
import { ModifyInfoService, MemberInfoResponse } from '../../shared/services/modify-info.service';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModifyPasswordDialogComponent } from '../../shared/components/dialogs/modify-password-dialog/modify-password-dialog.component';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import SHA256 from 'crypto-js/sha256';
import { Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import { ToastrService } from 'ngx-toastr';

/**
 * 會員中心修改基本資料組件
 */
@Component({
  selector: 'app-modify-info',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    MatInput,
    MatIconButton,
    FormsModule,
    ReactiveFormsModule,
    MatError,
    MatButtonToggleGroup,
    MatButtonToggle,
    NgOptimizedImage,
    NgClass,
  ],
  templateUrl: './modify-info.component.html',
  styleUrl: './modify-info.component.scss',
})
export class ModifyInfoComponent implements OnInit {
  dialog: MatDialog = inject(MatDialog);
  devService: DevService = inject(DevService);
  modifyInfoService: ModifyInfoService = inject(ModifyInfoService);
  translateService: TranslateService = inject(TranslateService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  cdr = inject(ChangeDetectorRef);
  router: Router = inject(Router);
  authService: AuthService = inject(AuthService);
  toastr: ToastrService = inject(ToastrService);

  gender: string = ''; // 性別
  birthday: string = ''; // 生日(YYYY/MM/DD)
  name: string = ''; // 姓名
  idNumber: string = ''; // 身分證字號
  phone?: string; // 手機
  email: string = ''; // 信箱
  chartNo: string = ''; // 病歷號

  currentPasswordControl = new FormControl('', [Validators.required]); // 當前密碼
  isPasswordVisible: boolean = false; // 當前密碼是否可見
  showPasswordError: boolean = false; // 是否顯示當前密碼錯誤訊息
  passwordErrorMessage: string = ''; // 當前密碼錯誤訊息
  isPasswordChangeDisabled: boolean = false; // 是否禁用變更密碼按鈕

  ngOnInit(): void {
    // TODO 取得登入信息
    this.gender = this.sharedStorageService.get('createMemberRequest')?.gender ?? '';
    this.birthday = moment(this.sharedStorageService.get('createMemberRequest')?.birthday).format(DATE_FORMAT) ?? '';
    this.name = this.sharedStorageService.get('createMemberRequest')?.name ?? '';
    this.idNumber = this.sharedStorageService.get('createMemberRequest')?.idNo ?? '';
    this.phone = this.sharedStorageService.get('createMemberRequest')?.cell ?? '';
    this.email = this.sharedStorageService.get('createMemberRequest')?.email ?? '';
    this.chartNo = this.sharedStorageService.get('createMemberRequest')?.chartNo ?? '';
    // this.checkPasswordChangeLimit();
    this.verifyMemberStatus();
  }

  // 開啟修改姓名dialog
  openModifyNameDialog() {
    const modifyNameDialog = this.dialog.open(ModifyNameDialogComponent, {
      autoFocus: false,
      data: {
        idNo: this.idNumber,
        name: '',
        chartNo: this.chartNo ?? '', // TODO 病例號
      },
    });

    modifyNameDialog.afterClosed().subscribe((result: string) => {
      if (!result) return; // 如果沒有結果，則不更新
      // 儲存更新後的姓名
      this.name = result;

      // 更新 sharedStorageService 中的資料
      const memberRequest = this.sharedStorageService.get('createMemberRequest');
      if (memberRequest) {
        memberRequest.name = result;
        this.sharedStorageService.set('createMemberRequest', memberRequest);
      }
      modifyNameDialog.close();
    });
  }

  // 開啟新增/修改手機dialog
  openSavePhoneDialog() {
    const savePhoneDialog = this.dialog.open(SavePhoneDialogComponent, {
      autoFocus: false,
      data: {
        isCreate: !this.phone,
        idNo: this.idNumber,
        chartNo: this.chartNo ?? '',
      },
    });

    savePhoneDialog.afterClosed().subscribe((result: string) => {
      if (!result) return; // 如果沒有結果，則不更新
      // 儲存更新後的手機號碼
      this.phone = result;

      // 更新 sharedStorageService 中的資料
      const memberRequest = this.sharedStorageService.get('createMemberRequest');
      if (memberRequest) {
        memberRequest.cell = result;
        this.sharedStorageService.set('createMemberRequest', memberRequest);
      }
      savePhoneDialog.close();
    });
  }

  // 開啟修改信箱dialog
  openModifyEmailDialog() {
    const modifyEmailDialog = this.dialog.open(ModifyEmailDialogComponent, {
      autoFocus: false,
      data: {
        idNo: this.idNumber,
        chartNo: this.chartNo ?? '', // TODO 病例號
        email: this.email,
      },
    });

    modifyEmailDialog.afterClosed().subscribe((result: string) => {
      if (!result) return; // 如果沒有結果，則不更新
      // 儲存更新後的信箱
      this.email = result;

      // 更新 sharedStorageService 中的資料
      const memberRequest = this.sharedStorageService.get('createMemberRequest');
      if (memberRequest) {
        memberRequest.email = result;
        this.sharedStorageService.set('createMemberRequest', memberRequest);
      }
      modifyEmailDialog.close();
    });
  }

  // 切換password可見
  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  // 變更密碼
  modifyPassword() {
    if (this.isPasswordChangeDisabled) {
      return;
    }

    this.showPasswordError = true;
    
    if (this.currentPasswordControl.valid) {
      this.passwordErrorMessage = '';
      
      // 加密密碼
      const currentPassword = this.currentPasswordControl.value ?? '';
      const encryptedPassword = SHA256(currentPassword).toString();
      
      this.modifyInfoService
        .verifyCurrentPassword(encryptedPassword)
        .subscribe({
          next: (res) => {
            if (res.success) {
              // 驗證成功，開啟修改密碼dialog
              const modifyPasswordDialog = this.dialog.open(ModifyPasswordDialogComponent);
              modifyPasswordDialog.afterClosed().subscribe(result => {
                if (result === 'success') {
                  // 成功：顯示成功訊息並登出
                  this.showPasswordError = false;
                  // localStorage.setItem('lastPasswordChangeDate', new Date().toDateString());
                  // this.isPasswordChangeDisabled = true;
                  this.currentPasswordControl.setValue('');
                  this.logoutAfterPasswordChange();
                } else if (result === 'error') {
                  // 失敗：顯示失敗訊息（不登出）
                  this.toastr.error(
                    this.translateService.instant('modify-password-dialog.modify-error')
                  );
                }
                // 如果 result 是 null 或 undefined（使用者取消），則什麼都不做
              });
            } else {
              // 驗證失敗，顯示錯誤訊息
              this.showPasswordError = true;
              
              // 檢查是否為今日已修改密碼的錯誤
              if (res.errorMessage === '密碼一天僅可修改一次，請於隔日再嘗試') {
                // this.passwordErrorMessage = res.errorMessage;
                this.isPasswordChangeDisabled = true;
              } else if (res.statusCode === 'EC1009' || 
                        res.errorMessage === '原密碼驗證錯誤，請重新輸入') {
                // 密碼錯誤，但不禁用按鈕，讓用戶可以重新輸入
                this.passwordErrorMessage = res.errorMessage ?? '原密碼驗證錯誤，請重新輸入';
              } else {
                // 其他錯誤
                this.passwordErrorMessage = res.errorMessage ?? '';
              }
              
              console.log('驗證失敗，顯示錯誤訊息:', this.passwordErrorMessage);
            }
          },
          error: (error) => {
            console.error('驗證密碼失敗:', error);
          }
        });
    } else {
      this.showPasswordError = true;
      this.passwordErrorMessage = this.translateService.instant('error.password-error.required');
      return;
    }
  }

  // 檢查密碼變更限制
  // checkPasswordChangeLimit(): void {
  //   const lastChangeDate = localStorage.getItem('lastPasswordChangeDate');
  //   const today = new Date().toDateString();
  //   if (lastChangeDate === today) {
  //     this.isPasswordChangeDisabled = true;
  //   } 
  // }

  // 驗證會員狀態 (call CB-009)
  private verifyMemberStatus(): void {
    this.modifyInfoService.getMemberInfo().subscribe({
      next: (res: MemberInfoResponse) => {
        if (res.statusCode === 'EC1001') {
          this.showNotRegisteredAlert();
        }
      },
      error: (error) => console.error('驗證會員狀態失敗:', error)
    });
  }

  private showNotRegisteredAlert(): void {
    const message = this.translateService.instant('member-verify.not-registered-title');
    
    if (confirm(message)) {
      this.router.navigate(['/healthcheck/login'], {
        queryParams: { tab: '1' }
      });
    }
  }

  /**
   * 變更密碼後登出
   */
  private logoutAfterPasswordChange(): void {
    // 顯示提示訊息
    this.toastr.success(
      this.translateService.instant('modify-password-dialog.success-message')
    );
    // 不打token，直接清除
    // 執行登出（會自動清除 token 和 sharedStorage）
    // this.authService.performLogout().subscribe({
    //   next: (res) => {
    //     this.authService.clearToken();
    //     console.log('登出 API 成功:', res);
    //   },
    //   error: (error) => {
    //     this.authService.clearToken();
    //     console.error('登出 API 失敗:', error);
    //   }
    // });
    
    // 導向登入頁
    this.sharedStorageService.clear();
    this.sharedStorageService.remove('fromIdLogin');
    this.sharedStorageService.remove('myReservation');
    this.sharedStorageService.remove('savedQuestionnaireData');
    this.authService.clearToken();
    this.router.navigate(['/healthcheck/login']);
  }

  protected readonly API_PARAMS = API_PARAMS;
}
